import { useCallback, useMemo, useState } from "react";

import { Box } from "../../components/ui/box";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import { useWidgetRegistry } from "./registry-context";
import { SettingsFields } from "./settings-fields";
import type { WidgetConfig, WidgetInstance, WidgetSettingsGroup } from "./types";

/**
 * The instance-level settings editor: resolves the instance's config through
 * the registry, then renders the module's custom `panel` — or its declarative
 * `settings` schema through `SettingsFields` — over a local draft. It omits
 * the Title field on purpose: a host's editor chrome (breadcrumb, preview
 * title) owns title editing. The draft is the source of truth for the form;
 * each edit updates it instantly and persists a per-key patch (not the whole
 * blob — avoids clobbering concurrent edits). Seeded once from the resolved
 * config: remount per instance via `key`, so no reset effect.
 */
export function WidgetSettingsPanel({
  instance,
  canManage,
  onConfigChange,
}: {
  instance: WidgetInstance;
  canManage: boolean;
  onConfigChange: (id: string, patch: WidgetConfig) => void;
}) {
  const registry = useWidgetRegistry();
  const def = registry.get(instance.type);

  const resolved = useMemo(
    () => registry.resolveConfig(def, instance.config as WidgetConfig, instance.schemaVersion ?? 1),
    [registry, def, instance.config, instance.schemaVersion],
  );
  const [draft, setDraft] = useState<WidgetConfig>(resolved);

  const commit = useCallback(
    (key: string, value: unknown) => {
      setDraft((d) => ({ ...d, [key]: value }));
      onConfigChange(instance.id, { [key]: value });
    },
    [instance.id, onConfigChange],
  );
  const commitPatch = useCallback(
    (patch: WidgetConfig) => {
      setDraft((d) => ({ ...d, ...patch }));
      onConfigChange(instance.id, patch);
    },
    [instance.id, onConfigChange],
  );

  const Panel = def?.panel;
  const groups = useMemo<WidgetSettingsGroup[]>(() => {
    const schema = def?.settings;
    return typeof schema === "function" ? schema(draft) : (schema ?? []);
  }, [def, draft]);

  return (
    <ScrollArea>
      <Box paddingX="md" paddingY="sm">
        {Panel ? (
          <Panel config={draft as never} onChange={commitPatch} />
        ) : groups.length > 0 ? (
          <SettingsFields groups={groups} values={draft} onChange={commit} disabled={!canManage} />
        ) : (
          <Text size="sm" variant="muted">
            This widget has no settings.
          </Text>
        )}
      </Box>
    </ScrollArea>
  );
}
